import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isNotEmpty } from '@newfront-insurance/core';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useCallback, useEffect, useRef } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext, useDragHandlersRef } from './chat-input-context';
import { SlashCommandMenu, useSlashCommandMenu } from './slash-command-menu';
import { usePromptCommands } from '../../../../hooks/use-prompt-commands';
import {
  getPlainTextFromElement,
  saveCursorPosition,
  restoreCursorPosition,
} from '../../../../lib/contenteditable-utils';
import {
  findMatchingPromptCommand,
  hasValidPromptCommandBoundary,
  renderPromptCommandContent,
} from '../../../../lib/prompt-command-utils';
import {
  buildPromptCommandFromSlashCommand,
  isSlashPromptCommand,
  type PromptCommand,
} from '../../../../lib/slash-command-constants';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatInputTextareaProps {
  placeholder?: string;
  className?: string;
}

/**
 * Textarea component for chat input.
 * Handles text input, auto-height adjustment, and keyboard shortcuts.
 * Uses context to access input state and submit handler.
 * When slash commands are enabled, uses contenteditable for rich text rendering.
 */
export function ChatInputTextarea({ placeholder = 'How can I help you today?', className }: ChatInputTextareaProps) {
  const { input, setInput, editorRef, handleSubmit, status, selectedSlashCommand, setSelectedSlashCommand } =
    useChatInputContext();
  const { accountUuid, userUuid, additionalContext } = useChatOverlayContext();
  const { attachments } = useAttachments();
  const handlersRef = useDragHandlersRef();
  const { handlePaste } = handlersRef.current;
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const successfulAttachmentUuids = attachments
    .filter((attachment) => attachment.fileUuid && !attachment.uploadError)
    .map((attachment) => attachment.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids)
    ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
    : [];
  const hasUnsyncedAttachmentContext =
    successfulAttachmentUuids.length !== contextAttachmentFileUuids.length ||
    successfulAttachmentUuids.some((uuid) => !contextAttachmentFileUuids.includes(uuid));

  // Feature flags for prompt command autocomplete
  const isSlashCommandEnabled = useFeatureFlag('nova-slash-command', false);
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const isPromptCommandEnabled = isSlashCommandEnabled || isNovaSkillsEnabled;

  // Slash command menu state
  const slashCommandMenu = useSlashCommandMenu();
  const { commands } = usePromptCommands({
    accountUuid: isPromptCommandEnabled ? accountUuid : undefined,
    userUuid: isPromptCommandEnabled ? userUuid : undefined,
    includeSlashCommands: isSlashCommandEnabled,
    includeSkills: isNovaSkillsEnabled,
  });

  // Track if we're updating the content programmatically
  const isUpdatingRef = useRef(false);

  // Track if a slash command was just selected from the menu (to prevent Enter from submitting)
  const justSelectedCommandRef = useRef(false);

  const adjustHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = `${editorRef.current.scrollHeight + 2}px`;
    }
  }, [editorRef]);

  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '92px';
    }
  }, [editorRef]);

  // Update the contenteditable display
  const updateDisplay = useCallback(
    (text: string, command: PromptCommand | null) => {
      const editor = editorRef.current;
      if (!editor || !(editor instanceof HTMLDivElement)) return;

      isUpdatingRef.current = true;
      const cursorPos = saveCursorPosition(editor);
      editor.innerHTML = renderPromptCommandContent(text, command) || '<br>';
      restoreCursorPosition(editor, cursorPos);
      isUpdatingRef.current = false;
    },
    [editorRef],
  );

  // Handle input changes for contenteditable
  const handleContentEditableInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !(editor instanceof HTMLDivElement) || isUpdatingRef.current) return;

    const newValue = getPlainTextFromElement(editor);
    setInput(newValue);
    adjustHeight();

    // Auto-detect typed slash commands and update selection state
    const matchedCommand = findMatchingPromptCommand(newValue, commands);
    const currentCommandStillValid =
      selectedSlashCommand && hasValidPromptCommandBoundary(newValue, selectedSlashCommand.handle);

    // Handle slash command menu detection - don't reopen if a command is already selected
    if (newValue.startsWith('/') && !matchedCommand && !currentCommandStillValid) {
      if (!slashCommandMenu.isOpen) {
        slashCommandMenu.open(newValue);
      } else {
        slashCommandMenu.updateQuery(newValue);
      }
    } else if (slashCommandMenu.isOpen) {
      slashCommandMenu.close();
    }

    if (matchedCommand) {
      if (isSlashPromptCommand(matchedCommand)) {
        if (selectedSlashCommand?.uuid !== matchedCommand.slashCommand.uuid) {
          setSelectedSlashCommand(matchedCommand.slashCommand);
          slashCommandMenu.close();
        }
      } else if (selectedSlashCommand) {
        setSelectedSlashCommand(null);
        slashCommandMenu.close();
      }
      updateDisplay(newValue, matchedCommand);
    } else if (currentCommandStillValid) {
      // Keep current selection and display
      updateDisplay(newValue, selectedSlashCommand ? buildPromptCommandFromSlashCommand(selectedSlashCommand) : null);
    } else if (selectedSlashCommand) {
      // Clear invalid selection
      setSelectedSlashCommand(null);
      updateDisplay(newValue, null);
    }
  }, [
    editorRef,
    setInput,
    adjustHeight,
    commands,
    slashCommandMenu,
    selectedSlashCommand,
    setSelectedSlashCommand,
    updateDisplay,
  ]);

  // Handle slash command selection from menu
  const handleSlashCommandSelect = useCallback(
    (command: PromptCommand) => {
      const newInput = `/${command.handle} `;
      setInput(newInput);
      setSelectedSlashCommand(isSlashPromptCommand(command) ? command.slashCommand : null);
      slashCommandMenu.close();
      justSelectedCommandRef.current = true;

      // Update contenteditable content
      const editor = editorRef.current;
      if (editor instanceof HTMLDivElement) {
        editor.innerHTML = renderPromptCommandContent(newInput, command);
        // Place cursor right after the command handle + space, before the instruction placeholder
        const range = document.createRange();
        const placeholderEl = editor.querySelector('[data-instruction-placeholder]');
        if (placeholderEl) {
          range.setStartBefore(placeholderEl);
          range.collapse(true);
        } else {
          range.selectNodeContents(editor);
          range.collapse(false);
        }
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      editorRef.current?.focus();
    },
    [setInput, setSelectedSlashCommand, slashCommandMenu, editorRef],
  );

  const submitForm = useCallback(() => {
    if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
      return;
    }

    handleSubmit(undefined);
    resetHeight();
    // Clear contenteditable content
    const editor = editorRef.current;
    if (editor instanceof HTMLDivElement) {
      editor.innerHTML = '<br>';
    }
  }, [hasUploadingFiles, hasUnsyncedAttachmentContext, handleSubmit, resetHeight, editorRef]);

  // Sync input state to contenteditable when input changes externally
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !(editor instanceof HTMLDivElement)) return;

    const currentText = getPlainTextFromElement(editor);
    if (currentText !== input) {
      const matchedCommand =
        findMatchingPromptCommand(input, commands) ??
        (selectedSlashCommand ? buildPromptCommandFromSlashCommand(selectedSlashCommand) : null);
      updateDisplay(input, matchedCommand);
    }
  }, [input, commands, editorRef, selectedSlashCommand, updateDisplay]);

  // Initial height adjustment
  useEffect(() => {
    if (editorRef.current) {
      adjustHeight();
    }
  }, [adjustHeight, editorRef]);

  // Handle paste for contenteditable - strip formatting
  const handleContentEditablePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);

      // Also call the external paste handler for file handling
      if (handlePaste) {
        handlePaste(event as unknown as React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>);
      }
    },
    [handlePaste],
  );

  // Common styles for the editor
  const editorStyles = cn(
    'max-h-[calc(25dvh)] min-h-[24px] resize-none overflow-y-auto',
    'bg-background !text-base transition-colors',
    'min-h-[92px] border-none focus-within:border-none hover:focus-within:border-none',
    'rounded-[10px] px-3 py-2.5',
    'focus:ring-0 focus:outline-none',
    'break-words whitespace-pre-wrap',
    className,
    {
      'rounded-b-[0px] border-b-0': isNotEmpty(attachments),
    },
  );

  // Handle keydown for both modes
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      // When slash command menu is open, handle navigation keys
      if (isPromptCommandEnabled && slashCommandMenu.isOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          slashCommandMenu.close();
          return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab' || event.key === 'Enter') {
          // Prevent default to stop cursor movement and let SlashCommandMenu handle these
          event.preventDefault();
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        // Don't submit if a slash command was just selected from the menu via Enter
        if (justSelectedCommandRef.current) {
          justSelectedCommandRef.current = false;
          return;
        }
        if (status === 'ready' && !hasUploadingFiles && !hasUnsyncedAttachmentContext) {
          submitForm();
        }
      }
    },
    [
      isPromptCommandEnabled,
      slashCommandMenu.isOpen,
      status,
      hasUploadingFiles,
      hasUnsyncedAttachmentContext,
      submitForm,
    ],
  );

  // Use contenteditable when prompt commands are enabled
  if (isPromptCommandEnabled) {
    return (
      <div className="relative flex w-full flex-col gap-4">
        <SlashCommandMenu
          isOpen={slashCommandMenu.isOpen}
          onClose={slashCommandMenu.close}
          onSelect={handleSlashCommandSelect}
          commands={commands}
          searchQuery={slashCommandMenu.searchQuery}
          anchorRef={editorRef as React.RefObject<HTMLDivElement>}
        />
        <div
          ref={editorRef as React.RefObject<HTMLDivElement>}
          role="textbox"
          aria-label={placeholder}
          tabIndex={0}
          contentEditable
          data-testid="chat-input"
          data-placeholder={placeholder}
          className={cn(
            editorStyles,
            'empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]',
          )}
          onInput={handleContentEditableInput}
          onPaste={handleContentEditablePaste}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
        />
      </div>
    );
  }

  // Fallback to regular textarea when slash commands are disabled
  return (
    <div className="relative flex w-full flex-col gap-4">
      <textarea
        data-testid="chat-input"
        ref={editorRef as React.RefObject<HTMLTextAreaElement>}
        placeholder={placeholder}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          adjustHeight();
        }}
        onPaste={handlePaste as React.ClipboardEventHandler<HTMLTextAreaElement>}
        className={editorStyles}
        rows={2}
        autoFocus
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
